import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { dismissCard } from "../../services/progress-notifications/actions";
import useEtaCountdown from "../../hooks/useEtaCountdown";
import useClientEta from "../../hooks/useClientEta";
import useElapsedSeconds from "../../hooks/useElapsedSeconds";
import { elapsedLineText, etaLineText, overrunLineText } from "../../utils/progressEta";
import ErrorDetailModal from "./ErrorDetailModal";

// FontAwesome 5 (free / solid) naming — the repo's awesome-icons.css is FA5,
// so "fa-triangle-exclamation" / "fa-xmark" (FA6 names) render nothing.
const ICONS = {
	running: "fas fa-spinner",
	done: "fas fa-check",
	warning: "fas fa-exclamation-circle",
	error: "fas fa-exclamation-triangle",
};

const AUTO_DISMISS_MS = 6000;
const HOVER_RECHECK_MS = 1000;
const MAX_FAILED_ITEMS_SHOWN = 3;
// A time-driven bar never claims completion: the last stretch belongs to the terminal frame.
const TIMED_BAR_MAX_PERCENT = 95;

const SLIDE_IN = { x: 40, scale: 0.96, opacity: 0 };
const SLIDE_AT_REST = { x: 0, scale: 1, opacity: 1 };
const SLIDE_EXIT = { height: 0, marginTop: 0, opacity: 0 };

export default function Card({ card, onHide }) {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const timerRef = useRef(null);
	const cardRef = useRef(null);
	const [detailOpen, setDetailOpen] = useState(false);
	const [hovered, setHovered] = useState(false);
	const [itemsOpen, setItemsOpen] = useState(true);

	// Auto-dismiss done cards. Only "done" ever dismisses itself — warning
	// and error cards carry the record of what failed and must be closed
	// deliberately. Hovering pauses it; unhover restarts a full window
	// (simpler than remaining-time bookkeeping, and the card is being read
	// anyway). Cleared on state change too — if a frame flips state back to
	// running mid-timer we must not fire a stale dismiss.
	useEffect(() => {
		if (card.state !== "done" || hovered) {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
				timerRef.current = null;
			}
			return undefined;
		}
		const dismissUnlessPointedAt = () => {
			// A card can mount (or reflow) under a stationary pointer, which
			// fires no mouseenter — dismissing it there would yank it from
			// under the cursor mid-click. The CSS hover state is accurate
			// regardless of whether an event fired, so re-check at expiry.
			if (cardRef.current?.matches?.(":hover")) {
				timerRef.current = setTimeout(dismissUnlessPointedAt, HOVER_RECHECK_MS);
				return;
			}
			dispatch(dismissCard(card.kind, card.jobId));
		};
		timerRef.current = setTimeout(dismissUnlessPointedAt, AUTO_DISMISS_MS);
		return () => {
			if (timerRef.current) clearTimeout(timerRef.current);
			timerRef.current = null;
		};
	}, [card.state, card.kind, card.id, card.jobId, hovered, dispatch]);

	const onDismiss = () => dispatch(dismissCard(card.kind, card.jobId));
	const countdownEta = useEtaCountdown(card.etaSeconds, card.state === "running");
	const clientEta = useClientEta(card.done, card.total, card.startedAt, card.state === "running");
	// Prefer backend ETA; fall back to client-side extrapolation when
	// the backend has no history (first run for this user/jobKind).
	const effectiveEta = countdownEta ?? clientEta;

	const isRunning = card.state === "running";
	const isTerminalFailure = card.state === "warning" || card.state === "error";
	const etaText = etaLineText(card.state, card.done, effectiveEta, t);
	// No ETA to show (no backend estimate and no client-side extrapolation yet):
	// report elapsed time rather than an open-ended "Calculating…", which users
	// read as a stalled job.
	//
	// Gated on `!etaText` so the hook's 1s interval only exists when its value is
	// actually rendered — otherwise every card showing an ETA would re-render once
	// a second to discard the result.
	// The single-item bar is driven by elapsed / estimate, so it needs the ticker too.
	const elapsedSeconds = useElapsedSeconds(card.startedAt, isRunning && (!etaText || card.timedBar));
	// A positive backend estimate whose countdown reached 0 while the job still runs.
	const estimateSpent = isRunning && card.etaSeconds > 0 && countdownEta === 0;
	const elapsedText = etaText
		? null
		: estimateSpent
			? overrunLineText(card.state, elapsedSeconds, t)
			: elapsedLineText(card.state, elapsedSeconds, t);
	const runningPercent = card.timedBar
		? Math.min(TIMED_BAR_MAX_PERCENT, Math.round(((elapsedSeconds || 0) / card.etaSeconds) * 100))
		: card.percent;

	// While running, the target line becomes the live item ("Now: …") when
	// the slice carries per-item frames; errors keep showing the humanised
	// message in place of the target, as before. Warnings keep their
	// "M of T succeeded" target — a top-level error string must not make a
	// partial success read like a total failure (the modal still shows it).
	const targetLine = isRunning
		? card.currentItemLabel
			? t("progressNotifications.nowItem", {
					label: card.currentItemLabel,
					defaultValue: `Now: ${card.currentItemLabel}`,
				})
			: card.target
		: card.state === "error"
			? card.error || card.target
			: card.target;

	const failedItems = card.failedItems || [];
	const visibleFailedItems = failedItems.slice(0, MAX_FAILED_ITEMS_SHOWN);
	// Count against failedCount, not the list length: a rehydrated job carries
	// the counts but no per-item frames, so the list can be short (or empty)
	// while failures did happen — the hint must still account for them.
	const hiddenFailedCount = Math.max(
		0,
		Math.max(card.failedCount || 0, failedItems.length) - visibleFailedItems.length
	);

	// A finished library import can carry a report (per-object counters and
	// warnings) worth opening even though nothing failed.
	const hasImportReport = !!card.importReport && card.state !== "running";
	const warningCount = card.warningCount || 0;
	const hasErrorDetail =
		(isTerminalFailure &&
			(card.error || card.actionHint || card.rawError || failedItems.length > 0)) ||
		hasImportReport;

	// Single-shot kinds (portfolio simulation/copy, generative…) never carry
	// per-item counts — a green success card must not claim "✓ 0".
	const hasCounts =
		card.total > 0 || card.completedCount > 0 || card.failedCount > 0;

	// Chip strings are shared between the visible chips and the aria summary
	// (and a running card re-renders every ETA tick — don't re-interpolate).
	const chips = useMemo(() => {
		if (!hasCounts) return null;
		return {
			succeeded: t("progressNotifications.chips.succeeded", {
				n: card.completedCount,
				defaultValue: `${card.completedCount} succeeded`,
			}),
			failed:
				card.failedCount > 0
					? t("progressNotifications.chips.failed", {
							n: card.failedCount,
							defaultValue: `${card.failedCount} failed`,
						})
					: null,
			left:
				isRunning && card.total > 0
					? t("progressNotifications.chips.left", {
							n: card.remaining,
							defaultValue: `${card.remaining} left`,
						})
					: null,
		};
	}, [hasCounts, card.completedCount, card.failedCount, card.remaining, card.total, isRunning, t]);

	const chipSummary = chips
		? [chips.succeeded, chips.failed, chips.left].filter(Boolean).join(", ")
		: "";
	const ariaLabel = `${card.label}. ${targetLine}. ${chipSummary}. ${
		isRunning ? etaText || "" : ""
	}`.trim();

	return (
		<>
			<motion.div
				layout
				initial={SLIDE_IN}
				animate={SLIDE_AT_REST}
				exit={SLIDE_EXIT}
				transition={{
					duration: 0.35,
					ease: card.state === "done" ? [0.4, 0, 0.2, 1] : [0.2, 0.7, 0.2, 1],
				}}
				ref={cardRef}
				className={`pn-card pn-card--${card.state}`}
				role="status"
				aria-label={ariaLabel}
				data-testid={`progress-card-${card.kind}`}
				onMouseEnter={() => setHovered(true)}
				onMouseLeave={() => setHovered(false)}
			>
				<div className="pn-card__top">
					<span className="pn-card__icon" aria-hidden="true">
						<i className={ICONS[card.state]} />
					</span>
					<div className="pn-card__body">
						<div className="pn-card__label">{card.label}</div>
						<div className="pn-card__target">{targetLine}</div>
						{card.combinedRunHint && (
							<div className="pn-card__hint" data-testid="progress-card-combined-hint">
								{card.combinedRunHint}
							</div>
						)}
					</div>
					{failedItems.length > 0 && (
						<button
							type="button"
							className="pn-card__items-toggle"
							aria-expanded={itemsOpen}
							aria-label={t("progressNotifications.failedItemsToggle", {
								count: failedItems.length,
								defaultValue: `${failedItems.length} failed items`,
							})}
							onClick={() => setItemsOpen((open) => !open)}
							data-testid="progress-card-items-toggle"
						>
							<i className={`fas fa-chevron-${itemsOpen ? "up" : "down"}`} />
						</button>
					)}
					{isRunning ? (
						// Hiding a running card only removes the CARD — the Redux job
						// keeps receiving frames so failures can still surface (a
						// dismiss here would delete the job and silently swallow its
						// outcome). The old ✕ read as "cancel", which this is not.
						<button
							type="button"
							className="pn-card__hide"
							title={t(
								"progressNotifications.hideTooltip",
								"Keeps running in the background"
							)}
							onClick={() => onHide?.(card.id)}
							data-testid="progress-card-hide"
						>
							{t("progressNotifications.hide", "Hide")}
						</button>
					) : (
						<button
							type="button"
							className="pn-card__dismiss"
							aria-label={t("progressNotifications.dismiss", "Dismiss")}
							onClick={onDismiss}
							data-testid="progress-card-dismiss"
						>
							<i className="fas fa-times" />
						</button>
					)}
				</div>

				{/* "What to do" guidance — error state ONLY (warnings surface the
				    per-item list instead). */}
				{card.state === "error" && card.actionHint && (
					<div className="pn-card__advice" data-testid="progress-card-advice">
						<i className="fas fa-question-circle" aria-hidden="true" />
						<span>{card.actionHint}</span>
					</div>
				)}

				{/* The failed items, named — the data was always in Redux, the card
				    just never showed it. Capped; the modal lists everything. */}
				{failedItems.length > 0 && itemsOpen && (
					<ul className="pn-card__failed-list" data-testid="progress-card-failed-items">
						{visibleFailedItems.map((item, index) => (
							<li className="pn-card__failed-item" key={`${item.label}-${index}`} data-testid="progress-card-failed-item">
								<i className="fas fa-times" aria-hidden="true" />
								<span className="pn-card__failed-label">{item.label}</span>
								{item.message && (
									<span className="pn-card__failed-reason"> — {item.message}</span>
								)}
							</li>
						))}
						{hiddenFailedCount > 0 && (
							<li className="pn-card__failed-more" data-testid="progress-card-failed-more">
								{t("progressNotifications.moreItems", {
									n: hiddenFailedCount,
									defaultValue: `+${hiddenFailedCount} more`,
								})}
							</li>
						)}
					</ul>
				)}

				<div className="pn-card__meta">
					<span className="pn-card__chips">
						{chips && (
							<span className="pn-chip pn-chip--done" data-testid="progress-card-chip-done">
								✓ {card.completedCount}
							</span>
						)}
						{chips?.failed && (
							<span className="pn-chip pn-chip--failed" data-testid="progress-card-chip-failed">
								✗ {card.failedCount}
							</span>
						)}
						{chips?.left && (
							<span className="pn-chip pn-chip--left" data-testid="progress-card-chip-left">
								{chips.left}
							</span>
						)}
						{warningCount > 0 && !isRunning && (
							<span
								className="pn-chip pn-chip--warnings"
								data-testid="progress-card-chip-warnings"
								title={t("progressNotifications.chips.warningsTitle", "Warnings never stop the import")}
							>
								⚠ {t("progressNotifications.chips.warnings", {
									count: warningCount,
									n: warningCount,
									defaultValue: `${warningCount} warning(s)`,
								})}
							</span>
						)}
					</span>
					{isRunning && etaText ? (
						<span className="pn-card__eta-badge">
							<i className="fas fa-clock" aria-hidden="true" />
							{etaText}
						</span>
					) : isRunning && elapsedText ? (
						<span className="pn-card__eta-badge pn-card__eta-badge--shimmer">
							<i className="fas fa-hourglass-half" aria-hidden="true" />
							{elapsedText}
						</span>
					) : null}
				</div>

				<div className="pn-card__bar" aria-hidden="true">
					{isRunning && card.finalizing ? (
						<span
							className="pn-card__bar-fill pn-card__bar-fill--indeterminate"
							data-testid="progress-card-bar-finalizing"
						/>
					) : isRunning ? (
						<span
							className="pn-card__bar-fill"
							style={{ width: `${runningPercent}%` }}
							data-testid="progress-card-bar-running"
						/>
					) : (
						// Terminal: the bar tells the true outcome ratio instead of
						// filling green (or red) regardless of what happened.
						<>
							<span
								className="pn-card__bar-fill pn-card__bar-fill--done"
								style={{ width: `${card.completedPercent}%` }}
							/>
							<span
								className="pn-card__bar-fill pn-card__bar-fill--failed"
								style={{ width: `${card.failedPercent}%` }}
							/>
						</>
					)}
				</div>

				{hasErrorDetail && (
					<button
						type="button"
						className="pn-card__error-detail-btn"
						onClick={() => setDetailOpen(true)}
						data-testid="progress-card-error-detail"
					>
						<i className="fas fa-info-circle" aria-hidden="true" />
						{t("progressNotifications.seeDetails", "See details")}
					</button>
				)}
			</motion.div>

			{detailOpen && (
				<ErrorDetailModal card={card} onClose={() => setDetailOpen(false)} />
			)}
		</>
	);
}
